"use client";

import { ArrowRight, ShieldCheck, Eye, Lock, FileText, Users, TriangleAlert as AlertTriangle, Search, ShieldAlert } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const classes = [{
    code: "§ S.01",
    icon: ShieldCheck,
    title: "AI THREAT DETECTION",
    description: "Advanced machine learning algorithms that identify and neutralize cyber threats in real-time before they impact your operations.",
    duration: "24/7 ACTIVE"
  }, {
    code: "§ S.02",
    icon: Eye,
    title: "SECURITY MONITORING",
    description: "Continuous oversight of your digital infrastructure to ensure immediate response to any suspicious activity or unauthorized access.",
    duration: "24/7 ACTIVE"
  }, {
    code: "§ S.03",
    icon: Lock,
    title: "PHISHING PREVENTION",
    description: "Proactive defense mechanisms designed to block malicious emails and social engineering attempts targeting your employees.",
    duration: "REAL-TIME"
  }, {
    code: "§ S.04",
    icon: Search,
    title: "VULNERABILITY ASSESSMENTS",
    description: "Comprehensive audits to identify weaknesses in your network, applications, and systems, providing actionable remediation steps.",
    duration: "PERIODIC"
  }, {
    code: "§ S.05",
    icon: Users,
    title: "SECURITY AWARENESS",
    description: "Engaging training programs that empower your team to recognize and report potential security risks effectively.",
    duration: "ONGOING"
  }, {
    code: "§ S.06",
    icon: AlertTriangle,
    title: "INCIDENT RESPONSE",
    description: "Rapid deployment of expert teams to contain, investigate, and recover from security breaches with minimal downtime.",
    duration: "ON-DEMAND"
  }, {
    code: "§ S.07",
    icon: FileText,
    title: "SECURITY CONSULTING",
    description: "Strategic guidance to align your cybersecurity posture with industry best practices and specific business goals.",
    duration: "TAILORED"
  }, {
    code: "§ S.08",
    icon: ShieldAlert,
    title: "RISK & COMPLIANCE",
    description: "Ensuring your business meets regulatory standards while managing digital risks through robust governance frameworks.",
    duration: "CERTIFIED"
  }];
  return <section data-section-id="1291" id="servicedetail" className="bg-slate-50 text-slate-900 py-24 md:py-44 px-6 md:px-12 relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute top-20 right-0 md:right-12 select-none font-sans leading-none text-slate-900/[0.04]" style={{
      fontSize: "clamp(18rem, 32vw, 36rem)"
    }}>
        01
      </div>

      <div className="max-w-[1440px] mx-auto relative">
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 md:mb-28 transition-all duration-[520ms] ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4 mb-10">
              <span className="font-mono text-xs md:text-sm uppercase tracking-[0.18em] text-slate-900 font-medium">
                § 01 / OUR EXPERTISE
              </span>
              <span className="block w-10 h-px bg-slate-900" />
            </div>
            <h2 className="font-default uppercase text-slate-900 leading-[0.86]" style={{
            fontSize: "clamp(3rem, 8vw, 7.5rem)",
            letterSpacing: "-0.015em"
          }}><span className="!text-4xl">CYBERSECURITY </span><span className="font-default italic text-blue-600" style={{
              letterSpacing: '0.01em'
            }} contenteditable="false"><span className="!text-3xl">SOLUTIONS</span></span></h2>
          </div>

          <div className="lg:col-span-8 lg:pt-4 flex flex-col justify-end">
            <p className="font-default text-slate-600 max-w-2xl mb-8" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em"
          }}><br />InfinitySecure provides comprehensive, AI-driven protection for your digital assets. From proactive threat detection to incident response, we ensure your business remains secure, compliant, and resilient against modern cyber threats.</p>
            <div className="flex items-center gap-3">
              <span className="block w-2 h-2 bg-blue-600" />
              <span className="font-mono text-xs uppercase tracking-[0.14em] text-slate-600 font-medium">
                — 08 CORE SERVICES · SCALABLE PROTECTION
              </span>
            </div>
          </div>
        </div>

        <div className="relative mb-16 md:mb-20">
          <div className="h-px w-full bg-slate-200" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
          {classes.map((cls, i) => <ClassCard key={cls.title} data-index={i} index={i} code={cls.code} Icon={cls.icon} title={cls.title} description={cls.description} duration={cls.duration} />)}
        </div>
      </div>
    </section>;
}
function ClassCard({
  index,
  code,
  Icon,
  title,
  description,
  duration,
  ...rest
}: {
  index: number;
  code: string;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  duration: string;
  "data-index"?: number;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>();
  const delay = index % 4 * 80 + Math.floor(index / 4) * 40;
  return <div {...rest} ref={reveal.ref} className={`group relative bg-slate-50 p-8 md:p-10 transition-all duration-[520ms] ease-out hover:bg-white shadow-md ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionDelay: reveal.isVisible ? `${delay}ms` : "0ms"
  }}>
      <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 transition-all duration-300 ease-out group-hover:top-2 group-hover:left-2">
        <span className="block bg-blue-600 transition-all duration-300 ease-out" style={{
        width: "12px",
        height: "1.5px"
      }} />
        <span className="block bg-blue-600 transition-all duration-300 ease-out" style={{
        width: "1.5px",
        height: "12px"
      }} />
      </span>

      <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 transition-all duration-300 ease-out group-hover:bottom-2 group-hover:right-2">
        <span className="block bg-blue-600 transition-all duration-300 ease-out group-hover:w-[20px] group-hover:h-[1.5px] ml-auto" style={{
        width: "12px",
        height: "1.5px"
      }} />
        <span className="block bg-blue-600 transition-all duration-300 ease-out group-hover:w-[1.5px] group-hover:h-[20px] ml-auto" style={{
        width: "1.5px",
        height: "12px"
      }} />
      </span>

      <span aria-hidden="true" className="pointer-events-none absolute inset-0 border border-slate-200 transition-all duration-300 ease-out group-hover:border-[2.5px] group-hover:border-blue-600" />

      <div className="relative flex flex-col h-full min-h-[340px]">
        <div className="mb-8">
          <Icon className="text-slate-900 transition-colors duration-300 ease-out group-hover:text-blue-600" strokeWidth={1.5} />
        </div>

        <div className="mb-4">
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
            {code}
          </span>
        </div>

        <h3 className="font-default uppercase text-slate-900 mb-5" style={{
        fontSize: "clamp(1.5rem, 2.5vw, 2rem)",
        lineHeight: 0.92,
        letterSpacing: "0em"
      }}>
          {title}
        </h3>

        <p className="text-slate-600 mb-8 flex-grow" style={{
        fontFamily: "var(--font-sans)",
        fontSize: "0.9375rem",
        lineHeight: 1.65
      }}>
          {description}
        </p>

        <div className="flex items-end justify-between mt-auto pt-6 border-t border-slate-200">
          <Link to="/ContactPage" className="relative inline-flex items-center gap-2 group/link text-blue-600 hover:text-blue-700 font-default uppercase tracking-[0.04em] text-sm">
            <span className="relative">
              LEARN MORE
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-blue-600 transition-all duration-[220ms] ease-out group-hover/link:w-full" />
            </span>
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <span className="font-mono text-xs uppercase tracking-[0.14em] text-slate-500 font-medium">
            {duration}
          </span>
        </div>
      </div>
    </div>;
}